import { useMemo } from 'react'
import { fmtEur } from '@/shared/lib'
import { useWinners } from '@/shared/hooks'
import type { Winner } from '@/shared/types'

const FLAG_EMOJI: Record<string, string> = {
  DE: '🇩🇪', FR: '🇫🇷', GB: '🇬🇧', ES: '🇪🇸', IT: '🇮🇹', NL: '🇳🇱',
  AT: '🇦🇹', BE: '🇧🇪', PT: '🇵🇹', CH: '🇨🇭', IE: '🇮🇪', DK: '🇩🇰',
  SE: '🇸🇪', FI: '🇫🇮', NO: '🇳🇴', PL: '🇵🇱', CZ: '🇨🇿', LU: '🇱🇺',
  SK: '🇸🇰', HU: '🇭🇺',
}

function ReviewCard({ r }: { r: Winner }) {
  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center text-lg">
          {FLAG_EMOJI[r.country_code] ?? '🌍'}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">{r.name}</p>
          <div className="flex items-center gap-2">
            <span className="text-amber-400 text-xs">★★★★★</span>
            <span className="text-xs text-emerald-400 font-bold">+{fmtEur(r.amount)}</span>
          </div>
        </div>
      </div>
      <p className="text-sm text-gray-300 leading-relaxed">{r.review_text}</p>
      <p className="text-xs text-gray-600">
        Deal #{r.deal_number} · {new Date(r.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
      </p>
    </div>
  )
}

export function ReviewsPage() {
  const { data: winners = [] } = useWinners(200)
  const reviews = useMemo(() => winners.filter(w => w.review_text), [winners])

  return (
    <div className="py-6 space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-extrabold text-white">Trader Reviews</h1>
        <p className="text-gray-500">
          Real feedback from our community!
        </p>
        <div className="flex items-center justify-center gap-2">
          <span className="text-amber-400 text-lg">★★★★★</span>
          <span className="text-sm font-bold text-gray-400">4.9 average rating</span>
        </div>
      </div>

      {reviews.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-gray-500">Loading reviews...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {reviews.map(r => (
            <ReviewCard key={r.id} r={r} />
          ))}
        </div>
      )}
    </div>
  )
}
